'use client'

// frontend/src/hooks/useAutoRuns.ts
//
// The three reads behind the Auto Runs tab: the run list, the summary band, and
// one run in full for the audit panel.
//
// Hand-rolled `useState` / `useCallback` / `useEffect`, the same shape as
// `useRequisitions`, because this app has no SWR and no react-query and is not
// getting one for this tab (correction C15).
//
// Every response goes through the normalisers in the HR Auto contract file before
// it reaches a component, so a field the server renamed costs a default rather
// than a blank panel in front of a room.

import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import { warnIfTruncated } from '@/lib/pagination'
import {
  normalizeRunDetail,
  normalizeRunsSummary,
  type AutoRunDetail,
  type AutoRunListItem,
  type AutoRunListResponse,
  type AutoRunsSummary,
} from '@/components/hr-auto/types'

/** How many runs the tab asks for. The list is short by construction. */
export const AUTO_RUNS_PAGE_SIZE = 50

/**
 * Fetch recent runs, normalised.
 *
 * Exported as a plain function as well as through the hook because the HR Case
 * Management bridge (plan 05 section 5) needs the same rows from inside a context
 * provider's effect, and a second copy of the endpoint string is exactly how the
 * two would drift apart.
 */
export async function fetchAutoRuns(limit = AUTO_RUNS_PAGE_SIZE): Promise<AutoRunDetail[]> {
  const result = await apiClient.get<AutoRunListResponse | AutoRunListItem[]>(
    `/api/hr-auto/runs?limit=${limit}&page_size=${limit}`
  )

  // The house shape is `{ items, pagination }`, but a bare array is cheap to
  // tolerate and saves the tab from rendering empty over a spelling.
  const items = Array.isArray(result) ? result : (result?.items ?? [])
  if (!Array.isArray(result)) warnIfTruncated('useAutoRuns', result?.pagination)

  // Normalised as details rather than as list rows on purpose. A list row has no
  // messages, steps or artifacts and this fills those with empty lists, which
  // costs nothing; but IF the server embeds them in the listing, the HR Case
  // Management bridge gets the real transcript and step trail instead of a
  // stand-in. Dropping them here and discovering that later is the expensive way
  // round.
  return items.map((item) => normalizeRunDetail(item))
}

/** `GET /api/hr-auto/runs`. */
export function useAutoRuns(limit = AUTO_RUNS_PAGE_SIZE) {
  const [data, setData] = useState<AutoRunDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchAutoRuns(limit))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load auto runs')
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, loading, error, refetch }
}

/** `GET /api/hr-auto/runs/summary`. Feeds the four-up KPI band. */
export function useAutoRunsSummary() {
  const [data, setData] = useState<AutoRunsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await apiClient.get<unknown>('/api/hr-auto/runs/summary')
      setData(normalizeRunsSummary(result))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the run summary')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { data, loading, error, refetch }
}

/**
 * `GET /api/hr-auto/runs/{run_id}`, the whole audit trail for one run.
 *
 * A null id is the panel's closed state, so it resolves to no run and no request
 * rather than to a spurious error line.
 */
export function useAutoRunDetail(runId: string | null) {
  const [run, setRun] = useState<AutoRunDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (!runId) {
      setRun(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await apiClient.get<unknown>(`/api/hr-auto/runs/${runId}`)
      setRun(normalizeRunDetail(result))
    } catch (err) {
      setRun(null)
      setError(err instanceof Error ? err.message : 'Failed to load this run')
    } finally {
      setLoading(false)
    }
  }, [runId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { run, loading, error, refetch }
}
